import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, X, Send, Trash2, MapPin, ChevronLeft, ChevronRight, CheckCircle, AtSign, Reply, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttachmentAnnotator = ({ taskId, attachment, token, onClose }) => {
  const [annotations, setAnnotations] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [placingPin, setPlacingPin] = useState(false);
  const [pinPos, setPinPos] = useState(null);
  const [selectedAnn, setSelectedAnn] = useState(null);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIdx, setMentionIdx] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [expandedThreads, setExpandedThreads] = useState({});
  const [replyMentions, setReplyMentions] = useState(false);
  const [replyMentionQuery, setReplyMentionQuery] = useState('');
  const [replyMentionIdx, setReplyMentionIdx] = useState(0);
  const inputRef = useRef(null);
  const replyInputRef = useRef(null);
  const imgRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const isImage = attachment.content_type?.startsWith('image/');
  const isPdf = attachment.content_type === 'application/pdf';
  const viewUrl = `${API}/tasks/${taskId}/attachments/${attachment.file_id}/download`;
  const annUrl = `${API}/tasks/${taskId}/attachments/${attachment.file_id}/annotations`;

  const fetchAnnotations = useCallback(async () => {
    try { const res = await axios.get(annUrl, { headers }); setAnnotations(res.data); } catch {}
  }, [annUrl]);

  useEffect(() => { fetchAnnotations(); }, [fetchAnnotations]);
  useEffect(() => { const f = async () => { try { const res = await axios.get(`${API}/users`, { headers }); setUsers(res.data); } catch {} }; f(); }, []);

  const handleImageClick = (e) => {
    if (!placingPin) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    setPinPos({ x, y });
    setPlacingPin(false);
  };

  // ---- @mention logic for main input ----
  const handleCommentChange = (e) => {
    const val = e.target.value;
    setNewComment(val);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) { setShowMentions(true); setMentionQuery(atMatch[1].toLowerCase()); setMentionIdx(0); }
    else { setShowMentions(false); }
  };

  const handleMentionKeyDown = (e) => {
    if (!showMentions) return;
    const filtered = users.filter(u => u.name.toLowerCase().includes(mentionQuery));
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => Math.max(i - 1, 0)); }
    else if ((e.key === 'Enter' || e.key === 'Tab') && filtered.length > 0) { e.preventDefault(); insertMention(filtered[mentionIdx], inputRef, newComment, setNewComment, setShowMentions); }
    else if (e.key === 'Escape') { setShowMentions(false); }
  };

  // ---- @mention logic for reply input ----
  const handleReplyChange = (e) => {
    const val = e.target.value;
    setReplyText(val);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) { setReplyMentions(true); setReplyMentionQuery(atMatch[1].toLowerCase()); setReplyMentionIdx(0); }
    else { setReplyMentions(false); }
  };

  const handleReplyKeyDown = (e) => {
    if (!replyMentions) return;
    const filtered = users.filter(u => u.name.toLowerCase().includes(replyMentionQuery));
    if (e.key === 'ArrowDown') { e.preventDefault(); setReplyMentionIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setReplyMentionIdx(i => Math.max(i - 1, 0)); }
    else if ((e.key === 'Enter' || e.key === 'Tab') && filtered.length > 0) { e.preventDefault(); insertMention(filtered[replyMentionIdx], replyInputRef, replyText, setReplyText, setReplyMentions); }
    else if (e.key === 'Escape') { setReplyMentions(false); }
  };

  const insertMention = (user, ref, text, setText, closeFn) => {
    const textarea = ref.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textBefore = text.substring(0, cursorPos);
    const textAfter = text.substring(cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.substring(0, atIdx) + `@${user.name} ` + textAfter;
    setText(newText);
    closeFn(false);
    setTimeout(() => { const pos = atIdx + user.name.length + 2; textarea.setSelectionRange(pos, pos); textarea.focus(); }, 0);
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) { toast.error('Please enter a comment'); return; }
    if (!pinPos && isImage) { toast.error('Click on the document to place a pin first'); return; }
    try {
      await axios.post(annUrl, { content: newComment, page, x: pinPos?.x || 0, y: pinPos?.y || 0 }, { headers });
      toast.success('Annotation added');
      setNewComment(''); setPinPos(null); fetchAnnotations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReply = async (annotationId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${annUrl}/${annotationId}/replies`, { content: replyText }, { headers });
      toast.success('Reply added');
      setReplyText(''); setReplyingTo(null); setReplyMentions(false);
      setExpandedThreads(prev => ({ ...prev, [annotationId]: true }));
      fetchAnnotations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteReply = async (annotationId, replyId) => {
    try {
      await axios.delete(`${annUrl}/${annotationId}/replies/${replyId}`, { headers });
      toast.success('Reply deleted'); fetchAnnotations();
    } catch { toast.error('Failed'); }
  };

  const handleResolve = async (annId, resolved) => {
    try { await axios.put(`${annUrl}/${annId}`, { resolved: !resolved }, { headers }); fetchAnnotations(); } catch { toast.error('Failed'); }
  };

  const handleDelete = async (annId) => {
    try { await axios.delete(`${annUrl}/${annId}`, { headers }); toast.success('Deleted'); fetchAnnotations(); if (selectedAnn === annId) setSelectedAnn(null); } catch { toast.error('Failed'); }
  };

  const toggleThread = (annId) => setExpandedThreads(prev => ({ ...prev, [annId]: !prev[annId] }));

  const renderContent = (text) => {
    const parts = text.split(/(@[A-Z][\w]*(?:\s+[A-Z0-9][\w]*)*)/g);
    return parts.map((part, i) => part.startsWith('@') ? <span key={i} className="font-semibold text-[#002FA7]">{part}</span> : <span key={i}>{part}</span>);
  };

  const MentionDropdown = ({ show, query, idx, onSelect }) => {
    if (!show) return null;
    const filtered = users.filter(u => u.name.toLowerCase().includes(query)).slice(0, 8);
    if (filtered.length === 0) return null;
    return (
      <div className="absolute bottom-full left-0 w-full mb-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[140px] overflow-y-auto z-10" data-testid="mention-dropdown">
        {filtered.map((u, i) => (
          <button key={u.user_id}
            className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 transition-colors ${i === idx ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'hover:bg-[#F3F4F6] text-[#4B5563]'}`}
            onClick={() => onSelect(u)} data-testid={`mention-option-${u.user_id}`}>
            <div className="w-4 h-4 rounded-full bg-[#002FA7]/15 flex items-center justify-center">
              <span className="text-[7px] font-bold text-[#002FA7]">{u.name.charAt(0).toUpperCase()}</span>
            </div>
            <span className="font-medium">{u.name}</span>
          </button>
        ))}
      </div>
    );
  };

  const pageAnnotations = annotations.filter(a => a.page === page);
  const unresolvedCount = annotations.filter(a => !a.resolved).length;
  const totalReplies = annotations.reduce((sum, a) => sum + (a.replies?.length || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex" data-testid="annotation-overlay">
      {/* Document Viewer */}
      <div className="flex-1 flex flex-col bg-[#1a1a1a] relative">
        <div className="flex items-center justify-between px-4 py-2 bg-[#0d0d0d] border-b border-[#333]">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white h-7" onClick={onClose} data-testid="close-annotator"><X className="w-4 h-4 mr-1" />Close</Button>
            <span className="text-xs text-[#999] truncate max-w-[300px]">{attachment.original_filename}</span>
            <Badge className="bg-[#002FA7]/20 text-[#7BA4FF] text-[9px]">{unresolvedCount} open</Badge>
            {totalReplies > 0 && <Badge className="bg-[#5856D6]/20 text-[#9B99FF] text-[9px]">{totalReplies} replies</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {isPdf && (
              <div className="flex items-center gap-1 text-white text-xs">
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white" onClick={() => setPage(p => Math.max(1, p - 1))}><ChevronLeft className="w-3 h-3" /></Button>
                <span>Page {page}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-white" onClick={() => setPage(p => p + 1)}><ChevronRight className="w-3 h-3" /></Button>
              </div>
            )}
            <Button size="sm" className={`h-7 text-xs ${placingPin ? 'bg-[#FF9500] hover:bg-[#E68600]' : 'bg-[#002FA7] hover:bg-[#0025a0]'}`}
              onClick={() => { setPlacingPin(!placingPin); setPinPos(null); }} data-testid="place-pin-btn">
              <MapPin className="w-3 h-3 mr-1" />{placingPin ? 'Cancel Pin' : 'Add Comment'}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {isImage && (
            <div className="relative inline-block max-w-full max-h-full" data-testid="annotatable-image">
              <img ref={imgRef} src={viewUrl} alt={attachment.original_filename}
                className={`max-w-full max-h-[calc(100vh-160px)] object-contain rounded ${placingPin ? 'cursor-crosshair' : 'cursor-default'}`}
                onClick={handleImageClick} data-testid="annotation-image" />
              {pageAnnotations.map((ann, i) => (
                <button key={ann.annotation_id}
                  className={`absolute w-6 h-6 -ml-3 -mt-3 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow-lg transition-transform hover:scale-125 ${selectedAnn === ann.annotation_id ? 'ring-2 ring-white scale-125' : ''} ${ann.resolved ? 'bg-[#34C759]' : 'bg-[#002FA7]'}`}
                  style={{ left: `${ann.x}%`, top: `${ann.y}%` }}
                  onClick={(e) => { e.stopPropagation(); setSelectedAnn(selectedAnn === ann.annotation_id ? null : ann.annotation_id); }}
                  data-testid={`pin-${ann.annotation_id}`}>
                  {ann.replies?.length > 0 ? <span>{i + 1}<span className="text-[6px]">+{ann.replies.length}</span></span> : i + 1}
                </button>
              ))}
              {pinPos && (
                <div className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-[#FF9500] flex items-center justify-center animate-pulse shadow-lg"
                  style={{ left: `${pinPos.x}%`, top: `${pinPos.y}%` }} data-testid="pending-pin">
                  <MapPin className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          )}
          {isPdf && (
            <div className="relative w-full h-full" data-testid="annotatable-pdf">
              <iframe src={`${viewUrl}#page=${page}`} title={attachment.original_filename} className="w-full h-full rounded bg-white" data-testid="annotation-pdf" />
            </div>
          )}
          {!isImage && !isPdf && (
            <div className="text-center text-[#999] p-8">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-[#555]" />
              <p className="text-sm mb-1">Document preview not available</p>
            </div>
          )}
        </div>
      </div>

      {/* Annotation Sidebar */}
      <div className="w-[360px] bg-white border-l border-[#E5E7EB] flex flex-col" data-testid="annotation-sidebar">
        <div className="px-4 py-3 border-b border-[#E5E7EB]">
          <h3 className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#002FA7]" />Comments ({annotations.length})
            {totalReplies > 0 && <span className="text-[10px] text-[#9CA3AF] font-normal">· {totalReplies} replies</span>}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="annotation-list">
          {annotations.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
              <p className="text-xs text-[#9CA3AF]">{isImage ? 'Click "Add Comment" then click on the document' : 'Add comments about this document'}</p>
              <p className="text-[10px] text-[#C4C4C4] mt-1">Use @name to mention · Others can reply to your comments</p>
            </div>
          ) : annotations.map((ann, i) => {
            const replies = ann.replies || [];
            const isExpanded = expandedThreads[ann.annotation_id];
            const isReplying = replyingTo === ann.annotation_id;
            return (
              <div key={ann.annotation_id} data-testid={`annotation-${ann.annotation_id}`}>
                {/* Main annotation */}
                <div className={`p-2.5 rounded-lg border transition-all cursor-pointer ${selectedAnn === ann.annotation_id ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-[#E5E7EB] hover:border-[#002FA7]/30'} ${ann.resolved ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedAnn(selectedAnn === ann.annotation_id ? null : ann.annotation_id)}>
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${ann.resolved ? 'bg-[#34C759]' : 'bg-[#002FA7]'}`}>{i + 1}</div>
                      <span className="text-[10px] font-semibold text-[#0A0A0A]">{ann.user_name}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button variant="ghost" size="icon" className="h-5 w-5" onClick={(e) => { e.stopPropagation(); handleResolve(ann.annotation_id, ann.resolved); }}
                        data-testid={`resolve-${ann.annotation_id}`}>
                        <CheckCircle className={`w-3 h-3 ${ann.resolved ? 'text-[#34C759]' : 'text-[#9CA3AF]'}`} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-[#FF3B30]" onClick={(e) => { e.stopPropagation(); handleDelete(ann.annotation_id); }}
                        data-testid={`delete-ann-${ann.annotation_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <p className={`text-xs text-[#4B5563] leading-relaxed ${ann.resolved ? 'line-through' : ''}`}>{renderContent(ann.content)}</p>
                  {ann.mentions?.length > 0 && (
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {ann.mentions.map((m, j) => (
                        <Badge key={j} className="text-[8px] bg-[#002FA7]/10 text-[#002FA7] px-1.5 py-0" data-testid={`mention-badge-${m.user_id}`}>
                          <AtSign className="w-2 h-2 mr-0.5" />{m.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-2 text-[9px] text-[#9CA3AF]">
                      <span>{format(new Date(ann.created_at), 'MMM d, h:mm a')}</span>
                      {ann.resolved && <Badge className="text-[8px] bg-[#34C759]/10 text-[#34C759] px-1 py-0">Resolved</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      {replies.length > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); toggleThread(ann.annotation_id); }}
                          className="text-[9px] text-[#5856D6] flex items-center gap-0.5 hover:underline" data-testid={`toggle-thread-${ann.annotation_id}`}>
                          {isExpanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setReplyingTo(isReplying ? null : ann.annotation_id); setReplyText(''); setReplyMentions(false);
                        setExpandedThreads(prev => ({ ...prev, [ann.annotation_id]: true })); }}
                        className="text-[9px] text-[#002FA7] flex items-center gap-0.5 hover:underline" data-testid={`reply-btn-${ann.annotation_id}`}>
                        <Reply className="w-2.5 h-2.5" />Reply
                      </button>
                    </div>
                  </div>
                </div>

                {/* Reply Thread */}
                {isExpanded && replies.length > 0 && (
                  <div className="ml-5 mt-1 space-y-1 border-l-2 border-[#E5E7EB] pl-2" data-testid={`thread-${ann.annotation_id}`}>
                    {replies.map((rpl) => (
                      <div key={rpl.reply_id} className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`reply-${rpl.reply_id}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-[10px] font-semibold text-[#0A0A0A]">{rpl.user_name}</span>
                          <Button variant="ghost" size="icon" className="h-4 w-4 text-[#FF3B30]"
                            onClick={() => handleDeleteReply(ann.annotation_id, rpl.reply_id)} data-testid={`delete-reply-${rpl.reply_id}`}>
                            <Trash2 className="w-2.5 h-2.5" />
                          </Button>
                        </div>
                        <p className="text-[11px] text-[#4B5563] leading-relaxed">{renderContent(rpl.content)}</p>
                        {rpl.mentions?.length > 0 && (
                          <div className="flex gap-1 mt-0.5 flex-wrap">
                            {rpl.mentions.map((m, j) => (
                              <Badge key={j} className="text-[7px] bg-[#002FA7]/10 text-[#002FA7] px-1 py-0">
                                <AtSign className="w-1.5 h-1.5 mr-0.5" />{m.name}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <span className="text-[8px] text-[#9CA3AF]">{format(new Date(rpl.created_at), 'MMM d, h:mm a')}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Input */}
                {isReplying && (
                  <div className="ml-5 mt-1 border-l-2 border-[#002FA7] pl-2 relative" data-testid={`reply-input-${ann.annotation_id}`}>
                    <div className="relative">
                      <textarea ref={replyInputRef} placeholder="Reply... Use @name to mention"
                        value={replyText} onChange={handleReplyChange} onKeyDown={(e) => {
                          handleReplyKeyDown(e);
                          if (e.key === 'Enter' && !e.shiftKey && !replyMentions) { e.preventDefault(); handleReply(ann.annotation_id); }
                        }}
                        rows={2} className="w-full text-[11px] rounded-md border border-[#E5E7EB] px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                        data-testid={`reply-textarea-${ann.annotation_id}`} />
                      <MentionDropdown show={replyMentions} query={replyMentionQuery} idx={replyMentionIdx}
                        onSelect={(u) => insertMention(u, replyInputRef, replyText, setReplyText, setReplyMentions)} />
                    </div>
                    <div className="flex gap-1 mt-1">
                      <Button size="sm" className="h-6 text-[9px] bg-[#002FA7] hover:bg-[#00227A] flex-1"
                        onClick={() => handleReply(ann.annotation_id)} disabled={!replyText.trim()} data-testid={`send-reply-${ann.annotation_id}`}>
                        <Send className="w-2.5 h-2.5 mr-1" />Reply
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[9px]" onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* New Comment Input */}
        <div className="p-3 border-t border-[#E5E7EB] space-y-2 relative" data-testid="new-comment-area">
          {pinPos && <div className="flex items-center gap-1 text-[10px] text-[#FF9500]"><MapPin className="w-3 h-3" />Pin at ({pinPos.x}%, {pinPos.y}%)</div>}
          <div className="relative">
            <textarea ref={inputRef}
              placeholder={isImage ? 'Place pin, then comment... @name to mention' : 'Comment... @name to mention'}
              value={newComment} onChange={handleCommentChange} onKeyDown={handleMentionKeyDown}
              rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              data-testid="annotation-input" />
            <MentionDropdown show={showMentions} query={mentionQuery} idx={mentionIdx}
              onSelect={(u) => insertMention(u, inputRef, newComment, setNewComment, setShowMentions)} />
          </div>
          <div className="flex items-center gap-2">
            <Button className="flex-1 bg-[#002FA7] hover:bg-[#00227A] h-8 text-xs" onClick={handleSubmit} disabled={!newComment.trim()} data-testid="submit-annotation-btn">
              <Send className="w-3 h-3 mr-1.5" />Post Comment
            </Button>
            <p className="text-[9px] text-[#9CA3AF] flex items-center gap-0.5"><AtSign className="w-2.5 h-2.5" />mention</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentAnnotator;
